
import { Vue, Component, Watch } from 'vue-property-decorator'
import PageHeader from '@/components/base/PageHeader.vue'
import ActiveFilters from '@/components/listview/ActiveFilters.vue'
import TableSettings from '@/types/tableSettings'
import Column from '@/types/column'
import axios from 'axios'
import Pagination from '@/types/pagination'
import _ from 'lodash'
import RevitElement from '@/types/revitElement'
import ls from '@/ls'

@Component({
  components: { ActiveFilters, PageHeader }
})
export default class RevitElements extends Vue {
  _ = _
  permissionNames = { show: 'elements.view', edit: 'elements.edit', delete: 'elements.delete' }
  functionNames = { getList: 'getRevitElementList' }
  getterNames = { list: 'getList', loading: 'loading' }
  localStorageName = 'revitElementTableSettings'

  columns = [
    { title: 'Id', dataIndex: 'id', key: 'id' },
    { title: 'Bouwnr', dataIndex: 'building_id', key: 'building_id' },
    { title: 'Naam', dataIndex: 'name_sales', key: 'name_sales' },
    { title: 'T. Naam', dataIndex: 'name', key: 'name' },
    { title: 'Rooms', dataIndex: 'room_ids', key: 'room_ids', customRender: (text:string[]):string => text.join(', ') },
    { title: 'Level', dataIndex: 'level', key: 'level' },
    { title: 'Groep Id', dataIndex: 'group_id', key: 'group_id', width: 100 },
    { title: 'Categorie', dataIndex: 'category', key: 'category' },
    { title: 'Assemblage code', dataIndex: 'assembly_code', key: 'assembly_code' }
  ]

  tableSettings:TableSettings = ls.get(this.localStorageName) || {
    pagination: { current: 1, pageSize: 100 },
    showSizeChanger: true,
    pageSizeOptions: ['25', '50', '100'],
    activeFilters: { '': [] },
    sort: null,
    search: null
  }

  mounted () : void {
    this.getRevitElements()
    this.onTableSettingsChange()
  }

  getRevitElements () : void {
    this.$store.dispatch('revitElementStore/getItemList', this.tableSettings)
  }

  handleTableChange (pagination:{ current:number, pageSize:number }, filters:{[key: string]: number[]|string[]}, sort:{ columnKey:string, order:string }) : void {
    // Remove key from filters if filter is an empty object.
    if (filters && Object.keys(filters).length) {
      Object.keys(filters).forEach((key) => {
        if (!filters[key] || !filters[key].length) {
          delete filters[key]

          this.columns.forEach((column:Column) => {
            if (column.key === key) {
              column.filteredValue = null
            }
          })
        }
      })
    }

    // Set pagination, filters and sorting on tableSettings.
    this.tableSettings.pagination = pagination
    this.tableSettings.activeFilters = filters
    this.tableSettings.sort = sort.order ? sort : null

    // Get list based on filters and search term.
    this.getRevitElements()
  }

  handleSearchChange () : void {
    // Set page to 1 because of new search but retain filters and sorting.
    this.tableSettings.pagination.current = 1

    // Get list based on filters and search term.
    this.getRevitElements()
  }

  onRowClick (rowData:{id:number}) : { on: { click: () => void }} {
    return {
      on: {
        click: () => {
          const keyboard = window.event as KeyboardEvent
          const params = { elementId: rowData.id.toString() }
          const route = this.$router.resolve({ name: 'revitElementShow', params: params })
          window.open(route.href, keyboard.ctrlKey ? '_blank' : '_self')
        }
      }
    }
  }

  get loading () : boolean {
    return this.$store.getters['revitElementStore/loading'].list
  }

  get itemList () : { data: RevitElement[], meta: { per_page:string, total:number } } {
    return this.$store.getters['revitElementStore/list']
  }

  get pagination () : Pagination {
    return {
      current: this.tableSettings.pagination.current,
      defaultPageSize: this.tableSettings.pagination.pageSize,
      pageSize: parseInt(this.itemList.meta.per_page),
      total: this.itemList.meta.total,
      showTotal: (total, range) => `Element ${range[0]} t/m ${range[1]} van de ${total}`,
      showSizeChanger: this.tableSettings.showSizeChanger,
      pageSizeOptions: this.tableSettings.pageSizeOptions
    }
  }

  @Watch('tableSettings', { deep: true })
  onTableSettingsChange () : void {
    // On tableSettings change, store them in local storage.
    ls.set(this.localStorageName, this.tableSettings)

    axios.post(process.env.VUE_APP_API_URL + '/users/admin/settings', {
      settingName: this.localStorageName,
      settingValue: this.tableSettings
    })

    // Set the current active filters on the columns.
    this.columns.forEach((column:Column) => {
      // Set active filters.
      if (this.tableSettings.activeFilters && column.key in this.tableSettings.activeFilters) {
        column.filteredValue = this.tableSettings.activeFilters[column.key]
      }

      // Set active sort order.
      if (this.tableSettings.sort && column.key === this.tableSettings.sort.columnKey) {
        column.defaultSortOrder = this.tableSettings.sort.order
        column.sortOrder = this.tableSettings.sort.order
      } else {
        column.sortOrder = false
      }
    })
  }
}
